'use client'

import { App, Skeleton } from 'antd'
import { useRouter, useSearchParams } from 'next/navigation'
import { type ReactNode, useEffect, useEffectEvent, useRef } from 'react'
import type { LoginApi } from './login.api'
import type { EmailLinkTexts } from './texts'

/** Props of {@link EmailLinkLogin}. */
export interface EmailLinkLoginProps {
  api: LoginApi
  texts: EmailLinkTexts
  /** URL path of the login page, opened again when the link is invalid or expired. */
  loginPath: string
  /** Called once the session cookie is set; the app navigates on. */
  onLoggedIn: () => void
  /** Illustration below "check your inbox". */
  illustration?: ReactNode
}

/**
 * Page the emailed login link opens (`?token=`): trades the token for a session. Without a
 * token it tells the user to check their inbox, which is where {@link LoginForm} sends them
 * after emailing the link.
 */
export function EmailLinkLogin({
  api,
  texts,
  loginPath,
  onLoggedIn,
  illustration,
}: EmailLinkLoginProps) {
  const router = useRouter()
  const { message } = App.useApp()
  const token = useSearchParams().get('token')
  /** A token works once; this keeps a re-run effect (Strict Mode) from spending it twice. */
  const exchanged = useRef<string | null>(null)

  const exchange = useEffectEvent(async (value: string) => {
    const result = await api.loginWithEmailToken({ token: value })
    if (result.ok) {
      void message.success(texts.loggedIn)
      onLoggedIn()
    } else {
      void message.error(texts.linkExpired)
      router.push(loginPath)
    }
  })

  useEffect(() => {
    if (!token || exchanged.current === token) return
    exchanged.current = token
    void exchange(token)
  }, [token])

  if (token) {
    return (
      <>
        <h1>{texts.verifying}</h1>
        <Skeleton active />
      </>
    )
  }

  return (
    <>
      <h1>
        {texts.checkInboxTitle}
        <small>{texts.checkInboxText}</small>
      </h1>
      {illustration}
    </>
  )
}
